import { ArgumentSchema, CommandSchema, InitialValues, OptionSchema, Translations } from './crontabArgsForm.model'

const INIT_FLAG = 'crontabArgsInit'
const EMPTY_INITIAL: InitialValues = { cmd_arg: {}, cmd_opt: {}, cmd_flag: {} }

const safeParse = <T>(text: string | null | undefined, fallback: T): T => {
	if (!text) {
		return fallback
	}
	try {
		return JSON.parse(text) as T
	} catch {
		return fallback
	}
}

const getTranslations = (container: HTMLElement): Translations => ({
	arguments: container.dataset.textArguments || 'Arguments',
	options: container.dataset.textOptions || 'Options',
	arrayHint: container.dataset.textArrayHint || 'Comma-separated values',
	defaultHint: container.dataset.textDefaultHint || 'default',
})

const stringifyDefault = (value: unknown): string => {
	if (value === null || value === undefined) {
		return ''
	}
	if (typeof value === 'boolean') {
		return value ? 'true' : 'false'
	}
	if (Array.isArray(value)) {
		return value.join(', ')
	}
	return String(value)
}

const valueAsString = (value: unknown): string => {
	if (value === undefined || value === null) {
		return ''
	}
	if (Array.isArray(value)) {
		return value.join(', ')
	}
	return String(value)
}

const isTrueish = (value: unknown): boolean => value === true || value === 1 || value === '1'

const inputClass = 'w-full border border-gray-300 rounded px-2 py-1'
const helpClass = 'text-xs text-gray-500 mt-1'

const renderArgumentField = (arg: ArgumentSchema, current: unknown, t: Translations): HTMLElement => {
	const wrapper = document.createElement('div')
	wrapper.className = 'my-2'

	const label = document.createElement('label')
	label.className = 'block text-sm font-medium'
	label.textContent = arg.name + (arg.required ? ' *' : '')
	wrapper.appendChild(label)

	const input = document.createElement('input')
	input.type = 'text'
	input.name = `cmd_arg[${arg.name}]`
	input.className = inputClass
	input.value = valueAsString(current)

	if (arg.isArray) {
		input.placeholder = t.arrayHint
	}
	if (arg.default !== null && arg.default !== undefined && arg.default !== '') {
		input.placeholder = `${t.defaultHint}: ${stringifyDefault(arg.default)}`
	}

	wrapper.appendChild(input)

	if (arg.description) {
		const help = document.createElement('div')
		help.className = helpClass
		help.textContent = arg.description
		wrapper.appendChild(help)
	}

	return wrapper
}

const renderFlagField = (opt: OptionSchema, current: unknown): HTMLElement => {
	const wrapper = document.createElement('div')
	wrapper.className = 'my-2'

	const label = document.createElement('label')
	label.className = 'inline-flex items-center'

	const input = document.createElement('input')
	input.type = 'checkbox'
	input.name = `cmd_flag[--${opt.name}]`
	input.value = '1'
	input.checked = isTrueish(current)
	label.appendChild(input)

	const span = document.createElement('span')
	span.className = 'ml-1'
	span.textContent = '--' + opt.name + (opt.shortcut ? ` (-${opt.shortcut})` : '')
	label.appendChild(span)

	wrapper.appendChild(label)

	if (opt.description) {
		const help = document.createElement('div')
		help.className = `${helpClass} ml-6`
		help.textContent = opt.description
		wrapper.appendChild(help)
	}

	return wrapper
}

const renderValueOptionField = (opt: OptionSchema, current: unknown, t: Translations): HTMLElement => {
	const wrapper = document.createElement('div')
	wrapper.className = 'my-2'

	const label = document.createElement('label')
	label.className = 'block text-sm font-medium'
	label.textContent = '--' + opt.name + (opt.shortcut ? ` (-${opt.shortcut})` : '')
	wrapper.appendChild(label)

	const input = document.createElement('input')
	input.type = 'text'
	input.name = `cmd_opt[--${opt.name}]`
	input.className = inputClass
	input.value = valueAsString(current)

	if (opt.isArray) {
		input.placeholder = t.arrayHint
	}
	if (opt.default !== null && opt.default !== undefined && opt.default !== '' && opt.default !== false) {
		input.placeholder = `${t.defaultHint}: ${stringifyDefault(opt.default)}`
	}

	wrapper.appendChild(input)

	if (opt.description) {
		const help = document.createElement('div')
		help.className = helpClass
		help.textContent = opt.description
		wrapper.appendChild(help)
	}

	return wrapper
}

const renderSection = (title: string, body: HTMLElement[]): HTMLElement => {
	const section = document.createElement('div')
	section.className = 'my-3 p-3 border border-gray-200 rounded'

	const heading = document.createElement('div')
	heading.className = 'font-semibold mb-2'
	heading.textContent = title
	section.appendChild(heading)

	body.forEach((el) => section.appendChild(el))
	return section
}

const collectStructuredValues = (container: HTMLElement): Record<string, unknown> => {
	const result: Record<string, unknown> = {}

	container.querySelectorAll<HTMLInputElement>('input[name^="cmd_arg["]').forEach((input) => {
		const match = input.name.match(/^cmd_arg\[(.+)\]$/)
		if (match && input.value !== '') {
			result[match[1]] = input.value
		}
	})

	container.querySelectorAll<HTMLInputElement>('input[name^="cmd_opt["]').forEach((input) => {
		const match = input.name.match(/^cmd_opt\[(.+)\]$/)
		if (match && input.value !== '') {
			result[match[1]] = input.value
		}
	})

	container.querySelectorAll<HTMLInputElement>('input[name^="cmd_flag["]').forEach((input) => {
		const match = input.name.match(/^cmd_flag\[(.+)\]$/)
		if (match && input.checked) {
			result[match[1]] = true
		}
	})

	return result
}

const bucketArgsByCommand = (
	args: Record<string, unknown>,
	schema: CommandSchema | null,
): InitialValues & { raw: Record<string, unknown> } => {
	if (schema === null) {
		return { cmd_arg: {}, cmd_opt: {}, cmd_flag: {}, raw: { ...args } }
	}

	const argumentNames = new Set(schema.arguments.map((a) => a.name))
	const optionLookup: Record<string, OptionSchema> = {}
	schema.options.forEach((opt) => {
		optionLookup['--' + opt.name] = opt
		if (opt.shortcut) {
			optionLookup['-' + opt.shortcut] = opt
		}
	})

	const result: InitialValues & { raw: Record<string, unknown> } = {
		cmd_arg: {},
		cmd_opt: {},
		cmd_flag: {},
		raw: {},
	}

	Object.entries(args).forEach(([key, value]) => {
		if (!key.startsWith('-')) {
			if (argumentNames.has(key)) {
				result.cmd_arg[key] = value
			} else {
				result.raw[key] = value
			}
			return
		}

		const opt = optionLookup[key]
		if (!opt) {
			result.raw[key] = value
			return
		}

		if (!opt.acceptValue) {
			if (isTrueish(value)) {
				result.cmd_flag[key] = true
			} else {
				result.raw[key] = value
			}
			return
		}

		result.cmd_opt[key] = value
	})

	return result
}

const fillStructuredInputs = (
	container: HTMLElement,
	cmdArg: Record<string, unknown>,
	cmdOpt: Record<string, unknown>,
	cmdFlag: Record<string, unknown>,
): void => {
	container.querySelectorAll<HTMLInputElement>('input[name^="cmd_arg["], input[name^="cmd_opt["]').forEach((input) => {
		input.value = ''
	})

	container.querySelectorAll<HTMLInputElement>('input[name^="cmd_flag["]').forEach((input) => {
		input.checked = false
	})

	Object.entries(cmdArg).forEach(([name, value]) => {
		const input = container.querySelector<HTMLInputElement>(`input[name="cmd_arg[${name}]"]`)
		if (input) {
			input.value = valueAsString(value)
		}
	})

	Object.entries(cmdOpt).forEach(([name, value]) => {
		const input = container.querySelector<HTMLInputElement>(`input[name="cmd_opt[${name}]"]`)
		if (input) {
			input.value = valueAsString(value)
		}
	})

	Object.entries(cmdFlag).forEach(([name, value]) => {
		const input = container.querySelector<HTMLInputElement>(`input[name="cmd_flag[${name}]"]`)
		if (input) {
			input.checked = isTrueish(value)
		}
	})
}

const stringifyArgs = (args: Record<string, unknown>): string =>
	Object.keys(args).length === 0 ? '' : JSON.stringify(args, null, 2)

const initForm = (root: HTMLElement): void => {
	if (root.dataset[INIT_FLAG] === '1') {
		return
	}
	root.dataset[INIT_FLAG] = '1'

	const schemasEl = root.querySelector<HTMLScriptElement>('[data-cmd-schemas]')
	const initialEl = root.querySelector<HTMLScriptElement>('[data-cmd-initial]')
	const select = root.querySelector<HTMLSelectElement>('select[name="command"]')
	const container = root.querySelector<HTMLElement>('[data-cmd-args-container]')
	const rawToggle = root.querySelector<HTMLInputElement>('[data-cmd-raw-toggle]')
	const rawArgsWrapper = root.querySelector<HTMLElement>('[data-cmd-raw-args]')
	const argumentsTextarea = root.querySelector<HTMLInputElement | HTMLTextAreaElement>('[name="arguments"]')

	if (!schemasEl || !select || !container) {
		return
	}

	const schemas = safeParse<Record<string, CommandSchema>>(schemasEl.textContent, {})
	const initial = safeParse<InitialValues>(initialEl?.textContent ?? null, EMPTY_INITIAL)
	const t = getTranslations(container)

	let useInitialNext = true

	const render = (commandName: string): void => {
		container.innerHTML = ''
		const schema = schemas[commandName]
		if (!schema) {
			return
		}

		const argValues = useInitialNext ? initial.cmd_arg : {}
		const optValues = useInitialNext ? initial.cmd_opt : {}
		const flagValues = useInitialNext ? initial.cmd_flag : {}
		useInitialNext = false

		if (schema.arguments.length > 0) {
			container.appendChild(
				renderSection(
					t.arguments,
					schema.arguments.map((arg) => renderArgumentField(arg, argValues[arg.name], t)),
				),
			)
		}

		if (schema.options.length > 0) {
			container.appendChild(
				renderSection(
					t.options,
					schema.options.map((opt) => {
						const key = '--' + opt.name
						return opt.acceptValue
							? renderValueOptionField(opt, optValues[key], t)
							: renderFlagField(opt, flagValues[key])
					}),
				),
			)
		}
	}

	const applyRawMode = (enabled: boolean): void => {
		container.style.display = enabled ? 'none' : ''
		if (rawArgsWrapper) {
			rawArgsWrapper.style.display = enabled ? '' : 'none'
		}
	}

	const onRawModeChange = (): void => {
		if (!rawToggle || !argumentsTextarea) {
			return
		}
		if (rawToggle.checked) {
			const structured = collectStructuredValues(container)
			const existing = safeParse<Record<string, unknown>>(argumentsTextarea.value, {})
			argumentsTextarea.value = stringifyArgs({ ...existing, ...structured })
		} else {
			const parsed = safeParse<Record<string, unknown>>(argumentsTextarea.value, {})
			const schema = schemas[select.value] ?? null
			const buckets = bucketArgsByCommand(parsed, schema)
			fillStructuredInputs(container, buckets.cmd_arg, buckets.cmd_opt, buckets.cmd_flag)
			argumentsTextarea.value = stringifyArgs(buckets.raw)
		}
		applyRawMode(rawToggle.checked)
	}

	render(select.value)

	if (rawToggle) {
		applyRawMode(rawToggle.checked)
		rawToggle.addEventListener('change', onRawModeChange)
	}

	select.addEventListener('change', () => render(select.value))
}

const init = (): void => {
	document.querySelectorAll<HTMLElement>('[data-crontab-args-form]').forEach(initForm)
}

export const CrontabArgsFormComponent = { init, initForm }
